import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { Box, Button, Checkbox, FormControlLabel, TextField } from '@mui/material';
import { DocumentType } from '@synergy/models';
import { FormattedMessage, useIntl } from 'react-intl';
import { PaloModal } from '@synergy/components-palo';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';
import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { tokens } from '@synergy/theme-palo';

import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

const c = tokens.color;

export interface PaloStatusChangeDialogProps {
    open: boolean;
    onClose: () => void;
    /** What is about to happen, e.g. "Bestellung stornieren". */
    title: ReactNode;
    /** The consequence, in the user's words. */
    description: ReactNode;
    confirmLabel: ReactNode;
    /** Irreversible steps carry the warning colour. */
    destructive?: boolean;
    /** The document the remark belongs to; without it no remark is offered. */
    documentType?: DocumentType;
    documentId?: number;
    /**
     * Points the step may not pass without: each is shown as a box to tick, and
     * the confirmation stays closed until every one of them is ticked.
     */
    acknowledgements?: ReactNode[];
    /** An input the step needs of its own, shown above the points to tick. */
    children?: ReactNode;
    /** Holds the confirmation back for a reason only the caller knows. */
    confirmDisabled?: boolean;
    onConfirm: () => void;
}

/**
 * One dialog for the status changes a document has (#2132, wave 4).
 *
 * The study's point is that the remark belongs to the step, not to a free-text
 * field somewhere else: what someone writes here lands in the document's
 * history as an internal entry, next to the status change it explains.
 *
 * Where a step has something the user must take note of first — an open
 * internal note, a tax rate that does not fit the invoice address — that check
 * comes in as `acknowledgements` and holds the confirmation back until every
 * point is ticked. The checks themselves stay with the documents that know
 * them; this dialog only asks the questions.
 *
 * The study also shows a "notify the supplier" checkbox. That is deliberately
 * not here: who receives mail follows from the notification settings of the
 * accounts involved, and no endpoint takes an instruction to skip them — the
 * box would promise a choice nobody can honour. What did go out is visible
 * afterwards under "Nachrichten" in the history, which is the honest answer.
 */
export const PaloStatusChangeDialog = ({
    open,
    onClose,
    title,
    description,
    confirmLabel,
    destructive,
    documentType,
    documentId,
    acknowledgements,
    children,
    confirmDisabled,
    onConfirm,
}: PaloStatusChangeDialogProps): ReactElement => {
    const intl = useIntl();
    const dispatch = useThunkDispatch();
    const [remark, setRemark] = useState('');
    const [busy, setBusy] = useState(false);
    const [ticked, setTicked] = useState<boolean[]>([]);

    // A dialog that opens again must not still hold the last remark, and least
    // of all the ticks that let the last one through.
    useEffect(() => {
        if (open) {
            setRemark('');
            setBusy(false);
            setTicked([]);
        }
    }, [open]);

    const points = acknowledgements ?? [];
    const openPoints = points.some((_point, index) => !ticked[index]);
    // Written out, because DocumentType.Inquiry is 0: a truthiness check reads
    // an inquiry as "no document", drops the remark field and prints the 0.
    const takesRemark = undefined !== documentType && undefined !== documentId;

    const tick = (index: number) =>
        setTicked(previous => {
            const next = [...previous];
            next[index] = !next[index];

            return next;
        });

    const confirm = async () => {
        const text = remark.trim();

        setBusy(true);

        if (text && takesRemark) {
            const response = await dispatch((_dispatch, _getState, { api }) =>
                api.activities.addDocumentComment(documentType, documentId, text)
            );

            if (!response.success) {
                // The remark is the reason for the step; losing it silently and
                // carrying on would be the worse half of the job.
                dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
                setBusy(false);

                return;
            }
        }

        setBusy(false);
        onConfirm();
    };

    return (
        <PaloModal
            isVisible={open}
            handleClose={onClose}
            title={title}
            width="sm"
            themeContent={true}
            actionChildren={
                <>
                    <Button size="small" variant="outlined" onClick={onClose} disabled={busy}>
                        <FormattedMessage id="app.common.cancel" />
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        color={destructive ? 'error' : 'primary'}
                        onClick={confirm}
                        disabled={busy || openPoints || confirmDisabled}
                    >
                        {confirmLabel}
                    </Button>
                </>
            }
        >
            <Box sx={{ fontSize: '13px', lineHeight: '20px', color: c.ink }}>{description}</Box>

            {children && <Box sx={{ paddingTop: '14px' }}>{children}</Box>}

            {points.length > 0 && (
                <Box
                    sx={{
                        marginTop: '14px',
                        padding: '8px 10px',
                        backgroundColor: c.warnSoft,
                        border: `1px solid ${c.warnLine}`,
                        borderRadius: `${tokens.radius.small}px`,
                    }}
                >
                    {points.map((point, index) => (
                        <FormControlLabel
                            key={index}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={!!ticked[index]}
                                    onChange={() => tick(index)}
                                    disabled={busy}
                                    sx={{ padding: '4px', marginRight: '6px' }}
                                />
                            }
                            label={<Box sx={{ fontSize: '12px', lineHeight: '18px', color: c.ink }}>{point}</Box>}
                            sx={{ margin: 0, alignItems: 'flex-start' }}
                        />
                    ))}
                </Box>
            )}

            {takesRemark && (
                <Box sx={{ paddingTop: '14px' }}>
                    <Box sx={{ fontSize: '11px', color: c.ink2, paddingBottom: '4px' }}>
                        <FormattedMessage id="app.statusChange.remark" />
                    </Box>
                    <TextField
                        value={remark}
                        onChange={event => setRemark(event.target.value)}
                        placeholder={intl.formatMessage({ id: 'app.statusChange.remark.placeholder' })}
                        multiline={true}
                        minRows={2}
                        size="small"
                        fullWidth={true}
                        disabled={busy}
                    />
                </Box>
            )}
        </PaloModal>
    );
};
